import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customers as api } from "@/lib/api";
import { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Search, Building2 } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", address: "", vat_number: "" };

export default function Customers() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email || "", phone: c.phone || "", address: c.address || "", vat_number: c.vat_number || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.update(editing.id, form); toast.success("Customer updated"); }
      else { await api.create(form); toast.success("Customer created"); }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.remove(id); toast.success("Customer deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!list) return <Loading />;
  const filtered = list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Customers" subtitle="Your customer database and outstanding balances.">
        <Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-customer-button">
          <Plus className="w-4 h-4 mr-1.5" /> Add Customer
        </Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" description="Add your first customer to start creating invoices and quotes."
          action={<Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-add-customer"><Plus className="w-4 h-4 mr-1.5" /> Add Customer</Button>} />
      ) : (
        <Card>
          <div className="p-4 border-b border-slate-100 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-7 top-6.5" style={{ top: "1.6rem" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="pl-9 max-w-sm" data-testid="customer-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Customer</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">Contact</th>
                  <th className="px-5 py-3 font-semibold text-center">Invoices</th>
                  <th className="px-5 py-3 font-semibold text-right">Outstanding</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-3.5">
                      <button onClick={() => nav(`/customers/${c.id}`)} className="flex items-center gap-3 text-left" data-testid={`open-customer-${c.id}`}>
                        <span className="w-9 h-9 rounded-lg bg-[#F1F5F9] text-[#0B132B] flex items-center justify-center"><Building2 className="w-4 h-4" /></span>
                        <span>
                          <span className="block font-semibold text-[#0B132B]">{c.name}</span>
                          {c.vat_number && <span className="block text-xs text-slate-400">VAT {c.vat_number}</span>}
                        </span>
                      </button>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">
                      <div>{c.email || "—"}</div>
                      <div className="text-xs text-slate-400">{c.phone || ""}</div>
                    </td>
                    <td className="px-5 py-3.5 text-center text-slate-600">{c.invoice_count}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-[#0B132B]">{money(c.outstanding)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                        <ConfirmDelete onConfirm={() => remove(c.id)} title="Delete customer?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Name *</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="customer-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="customer-email-input" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="customer-phone-input" /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} className="mt-1.5" data-testid="customer-address-input" /></div>
            <div><Label>VAT Number</Label><Input value={form.vat_number} onChange={set("vat_number")} className="mt-1.5" data-testid="customer-vat-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-customer-button">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
